import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaUsers, FaDonate, FaBullhorn, FaBan } from "react-icons/fa";
import { IoClose, IoCheckmarkCircleOutline, IoTrash, IoBan } from "react-icons/io5";
import {
  fetchAllUsers,
  updateUser,
  deleteUser,
  banUser,
  unbanUser,
} from "../api/apiServices";
import { toast } from "react-toastify";

const ITEMS_PER_PAGE = 20;

const ROLE_COLORS = {
  admin:   "bg-purple-100 text-purple-700",
  vendor:  "bg-blue-100 text-blue-700",
  rider:   "bg-orange-100 text-orange-700",
  user:    "bg-gray-100 text-gray-600",
  default: "bg-gray-100 text-gray-600",
};

const roleColor = (role) => ROLE_COLORS[role] || ROLE_COLORS.default;

const initials = (u) =>
  `${u?.firstName?.[0] || ""}${u?.lastName?.[0] || ""}`.toUpperCase() || "?";

export default function UsersPage() {
  const [allData, setAllData]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [page, setPage]                 = useState(1);
  const [search, setSearch]             = useState("");
  const [roleFilter, setRoleFilter]     = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingId, setDeletingId]     = useState(null);
  const [banningId, setBanningId]       = useState(null);
  const [editFields, setEditFields]     = useState({});
  const [updating, setUpdating]         = useState(false);
  const [banReason, setBanReason]       = useState("");

  const fetchUsers = async () => {
    try {
      const res = await fetchAllUsers({ page: 1, limit: 1000 });
      setAllData(res);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    if (selectedUser) {
      setEditFields({
        firstName:   selectedUser.firstName || "",
        lastName:    selectedUser.lastName  || "",
        email:       selectedUser.email     || "",
        phoneNumber: selectedUser.phoneNumber || "",
        role:        selectedUser.role       || "user",
      });
      setBanReason("");
    }
  }, [selectedUser]);

  const users = allData?.users || [];
  const stats = allData?.stats || {};

  const filteredUsers = users.filter((u) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      u.firstName?.toLowerCase().includes(q) ||
      u.lastName?.toLowerCase().includes(q)  ||
      u.email?.toLowerCase().includes(q)      ||
      u.phoneNumber?.includes(q)              ||
      u.userId?.toLowerCase().includes(q);
    const matchRole = !roleFilter || u.role === roleFilter;
    return matchSearch && matchRole;
  });

  const totalPages     = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );

  const handleUpdate = async () => {
    try {
      setUpdating(true);
      const updated = await updateUser(selectedUser.userId, editFields);
      toast.success("User updated successfully");
      setAllData((prev) => ({
        ...prev,
        users: prev.users.map((u) =>
          u.userId === selectedUser.userId ? { ...u, ...updated.user } : u
        ),
      }));
      setSelectedUser(null);
    } catch (err) {
      toast.error(err.response?.data?.error || "Update failed");
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Delete this user? This cannot be undone.")) return;
    try {
      setDeletingId(userId);
      setSelectedUser(null);
      await deleteUser(userId);
      setAllData((prev) => ({
        ...prev,
        users: prev.users.filter((u) => u.userId !== userId),
        stats: { ...prev.stats, totalUsers: (prev.stats?.totalUsers || 1) - 1 },
      }));
      toast.success("User deleted");
    } catch (err) {
      toast.error("Failed to delete user");
    } finally {
      setDeletingId(null);
    }
  };

  const handleBan = async (userId) => {
    if (!window.confirm("Ban this user?")) return;
    try {
      setBanningId(userId);
      const res = await banUser(userId, banReason || "Admin action");
      toast.success("User banned");
      setAllData((prev) => ({
        ...prev,
        users: prev.users.map((u) =>
          u.userId === userId ? { ...u, isBanned: true } : u
        ),
      }));
      setSelectedUser((prev) => (prev?.userId === userId ? { ...prev, isBanned: true } : prev));
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to ban user");
    } finally {
      setBanningId(null);
    }
  };

  const handleUnban = async (userId) => {
    if (!window.confirm("Unban this user?")) return;
    try {
      setBanningId(userId);
      await unbanUser(userId);
      toast.success("User unbanned");
      setAllData((prev) => ({
        ...prev,
        users: prev.users.map((u) =>
          u.userId === userId ? { ...u, isBanned: false } : u
        ),
      }));
      setSelectedUser((prev) => (prev?.userId === userId ? { ...prev, isBanned: false } : prev));
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to unban user");
    } finally {
      setBanningId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">

      {/* Hero */}
      <div className="relative h-[400px] bg-gradient-to-r from-indigo-600 to-purple-600 flex items-center justify-center text-white">
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4">User Management</h1>
          <p className="text-lg opacity-90">View, edit, ban, or delete platform users</p>
        </div>
      </div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-6xl mx-auto px-4 mt-16 grid grid-cols-1 md:grid-cols-4 gap-6"
      >
        {[
          { label: "Total Users",   value: stats.totalUsers   || 0, from: "from-blue-500",   to: "to-indigo-600",  icon: <FaUsers size={36} /> },
          { label: "Vendors",       value: stats.totalVendors || 0, from: "from-green-500",  to: "to-emerald-600", icon: <FaDonate size={36} /> },
          { label: "Riders",        value: stats.totalRiders  || 0, from: "from-orange-400", to: "to-orange-600",  icon: <FaBullhorn size={36} /> },
          { label: "Banned",        value: stats.bannedUsers  || 0, from: "from-red-500",    to: "to-rose-600",    icon: <FaBan size={36} /> },
        ].map((s) => (
          <div key={s.label} className={`p-6 bg-gradient-to-r ${s.from} ${s.to} text-white rounded-2xl shadow-lg`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">{s.label}</p>
                <h3 className="text-3xl font-bold">{s.value}</h3>
              </div>
              {s.icon}
            </div>
          </div>
        ))}
      </motion.div>

      {/* Users List */}
      <div className="max-w-6xl mx-auto px-4 mt-16 mb-20">
        <h2 className="text-2xl font-bold mb-6">Users</h2>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <input
            type="text"
            placeholder="Search by name, email, phone, or ID..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full md:w-1/2 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            <option value="">All Roles</option>
            <option value="user">Users</option>
            <option value="vendor">Vendors</option>
            <option value="rider">Riders</option>
            <option value="admin">Admins</option>
          </select>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading && (
            <p className="col-span-full text-center text-gray-400">Loading users...</p>
          )}
          {!loading && paginatedUsers.length === 0 && (
            <p className="col-span-full text-center text-gray-500">No users found</p>
          )}
          {paginatedUsers.map((user) => (
            <div
              key={user._id}
              onClick={() => setSelectedUser(user)}
              className="bg-white rounded-xl shadow-md p-5 hover:shadow-xl transition cursor-pointer relative"
            >
              {user.isBanned && (
                <span className="absolute top-3 right-3 px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full font-semibold">
                  Banned
                </span>
              )}

              {/* Avatar + Name */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-sm flex-shrink-0">
                  {initials(user)}
                </div>
                <div>
                  <p className="font-bold text-gray-800 leading-tight">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-gray-400">{user.userId}</p>
                </div>
              </div>

              {/* Details */}
              <div className="text-sm text-gray-600 space-y-1">
                <p>📧 {user.email || "—"}</p>
                <p>📱 {user.phoneNumber || "—"}</p>
              </div>

              {/* Footer */}
              <div className="flex justify-between items-center mt-4">
                <span className={`px-3 py-1 text-xs rounded-full font-semibold capitalize ${roleColor(user.role)}`}>
                  {user.role || "user"}
                </span>
                <p className="text-xs text-gray-400">
                  {new Date(user.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex justify-center items-center gap-4 mt-10">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            className={`px-4 py-2 rounded-lg font-semibold ${
              page === 1 ? "bg-gray-200 text-gray-400" : "bg-indigo-600 text-white hover:bg-indigo-700"
            }`}
          >
            Previous
          </button>
          <span className="text-gray-600 font-medium">
            Page {page} of {totalPages || 1}
          </span>
          <button
            disabled={page === totalPages || totalPages === 0}
            onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
            className={`px-4 py-2 rounded-lg font-semibold ${
              page === totalPages || totalPages === 0
                ? "bg-gray-200 text-gray-400"
                : "bg-indigo-600 text-white hover:bg-indigo-700"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[95vh] flex flex-col shadow-2xl overflow-hidden">

            {/* Modal Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 relative flex-shrink-0">
              <button
                onClick={() => setSelectedUser(null)}
                className="absolute top-4 right-4 text-white/80 hover:text-white"
              >
                <IoClose size={28} />
              </button>

              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-white/20 font-bold text-xl flex items-center justify-center flex-shrink-0">
                  {initials(selectedUser)}
                </div>
                <div>
                  <h3 className="text-2xl font-bold">
                    {selectedUser.firstName} {selectedUser.lastName}
                  </h3>
                  <p className="text-sm opacity-75">{selectedUser.userId}</p>
                </div>
                <div className="ml-auto flex gap-2 flex-wrap">
                  {selectedUser.isBanned && (
                    <span className="px-3 py-1 text-xs rounded-full bg-red-500 text-white font-semibold">Banned</span>
                  )}
                  <span className={`px-3 py-1 text-xs rounded-full font-semibold capitalize bg-white/20 text-white`}>
                    {selectedUser.role || "user"}
                  </span>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-8">

              {/* Profile Info */}
              <div>
                <h4 className="font-semibold text-lg mb-3 text-gray-800">Profile Info</h4>
                <div className="bg-gray-50 p-4 rounded-xl text-sm space-y-2">
                  <p><b>Email:</b> {selectedUser.email || "—"}</p>
                  <p><b>Phone:</b> {selectedUser.phoneNumber || "—"}</p>
                  <p><b>Joined:</b> {new Date(selectedUser.createdAt).toLocaleString()}</p>
                  {selectedUser.banReason && (
                    <p className="text-red-500"><b>Ban Reason:</b> {selectedUser.banReason}</p>
                  )}
                </div>
              </div>

              {/* Admin Edit */}
              <div className="bg-indigo-50 p-5 rounded-2xl border border-indigo-100">
                <h4 className="font-bold text-indigo-900 mb-4">🛠️ Admin Management</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 uppercase mb-1">First Name</label>
                    <input
                      value={editFields.firstName}
                      onChange={(e) => setEditFields({ ...editFields, firstName: e.target.value })}
                      className="w-full p-2.5 bg-white border border-indigo-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 uppercase mb-1">Last Name</label>
                    <input
                      value={editFields.lastName}
                      onChange={(e) => setEditFields({ ...editFields, lastName: e.target.value })}
                      className="w-full p-2.5 bg-white border border-indigo-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 uppercase mb-1">Email</label>
                    <input
                      value={editFields.email}
                      onChange={(e) => setEditFields({ ...editFields, email: e.target.value })}
                      className="w-full p-2.5 bg-white border border-indigo-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 uppercase mb-1">Phone</label>
                    <input
                      value={editFields.phoneNumber}
                      onChange={(e) => setEditFields({ ...editFields, phoneNumber: e.target.value })}
                      className="w-full p-2.5 bg-white border border-indigo-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-indigo-700 uppercase mb-1">Role</label>
                    <select
                      value={editFields.role}
                      onChange={(e) => setEditFields({ ...editFields, role: e.target.value })}
                      className="w-full p-2.5 bg-white border border-indigo-200 rounded-lg text-sm"
                    >
                      <option value="user">User</option>
                      <option value="vendor">Vendor</option>
                      <option value="rider">Rider</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  {!selectedUser.isBanned && (
                    <div>
                      <label className="block text-xs font-bold text-indigo-700 uppercase mb-1">Ban Reason</label>
                      <input
                        value={banReason}
                        onChange={(e) => setBanReason(e.target.value)}
                        placeholder="Optional reason..."
                        className="w-full p-2.5 bg-white border border-indigo-200 rounded-lg text-sm"
                      />
                    </div>
                  )}
                </div>

                <button
                  onClick={handleUpdate}
                  disabled={updating}
                  className="mt-4 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 rounded-lg transition-all disabled:bg-gray-400"
                >
                  {updating ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="border-t bg-gray-50 p-6 flex justify-between items-center flex-shrink-0 gap-3 flex-wrap">

              {/* Delete */}
              <button
                onClick={() => handleDelete(selectedUser.userId)}
                disabled={deletingId === selectedUser.userId}
                className={`px-6 py-3 rounded-xl font-semibold text-white transition-all ${
                  deletingId === selectedUser.userId
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {deletingId === selectedUser.userId ? "Deleting..." : "🗑️ Delete User"}
              </button>

              {/* Ban / Unban */}
              {selectedUser.isBanned ? (
                <button
                  onClick={() => handleUnban(selectedUser.userId)}
                  disabled={banningId === selectedUser.userId}
                  className="px-6 py-3 rounded-xl font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-all disabled:bg-gray-400 flex items-center gap-2"
                >
                  <IoCheckmarkCircleOutline size={20} />
                  {banningId === selectedUser.userId ? "Processing..." : "Unban User"}
                </button>
              ) : (
                <button
                  onClick={() => handleBan(selectedUser.userId)}
                  disabled={banningId === selectedUser.userId}
                  className="px-6 py-3 rounded-xl font-semibold text-white bg-orange-500 hover:bg-orange-600 transition-all disabled:bg-gray-400 flex items-center gap-2"
                >
                  <IoBan size={20} />
                  {banningId === selectedUser.userId ? "Processing..." : "Ban User"}
                </button>
              )}

              {/* Close */}
              <button
                onClick={() => setSelectedUser(null)}
                className="px-8 py-3 rounded-xl bg-gray-200 hover:bg-gray-300 font-semibold transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
